import { useMemo } from 'react';
import { useAccount } from '../../../contexts/account/AccountContexts';

/**
 * Extracts hostname from domain string (removes protocol, www, paths)
 * @param domain - Domain string (can be URL with protocol or just domain)
 * @returns Clean hostname (e.g., "facebook.com" from "https://www.facebook.com")
 */
const extractHostname = (domain: string): string => {
  try {
    // If domain already has protocol, use URL constructor
    if (domain.startsWith('http://') || domain.startsWith('https://')) {
      const url = new URL(domain);
      return url.hostname.replace(/^www\./, '');
    }
    // Otherwise, treat as hostname already and just remove www if present
    return domain.replace(/^www\./, '').split('/')[0];
  } catch {
    // If URL parsing fails, try to clean manually
    return domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
};

/**
 * Hook to get the current project's domain
 * @param projectId - Current project ID
 * @returns Project domain or undefined if not found
 */
export const useProjectDomain = (projectId: number | null): string | undefined => {
  const { projectResponse } = useAccount();
  
  return useMemo(() => {
    if (!projectResponse || !projectId) return undefined;
    const project = projectResponse.find(p => p.id === projectId);
    return project?.domain ? extractHostname(project.domain) : undefined;
  }, [projectResponse, projectId]);
};
